import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Copy, FileText, Trash2, Eye, RefreshCw, ShieldAlert } from 'lucide-react';
import DocumentDetailsModal from '@/components/DocumentDetailsModal';

const reasonStyles = {
  'Archivo idéntico (mismo contenido)': 'bg-rose-100 text-rose-700 border-rose-300',
  'Mismo número de documento': 'bg-amber-100 text-amber-700 border-amber-300',
  'Mismo tercero, valor y fecha': 'bg-orange-100 text-orange-700 border-orange-300',
};

const Duplicates = () => {
  const { token, API } = useAuth();
  const [groups, setGroups] = useState([]);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [detailDocId, setDetailDocId] = useState(null);

  const fetchDuplicates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data.groups || []);
      setTotalDuplicates(response.data.total_duplicates || 0);
    } catch (error) {
      toast.error('Error al cargar duplicados');
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const deleteDoc = async (docId, filename) => {
    if (!window.confirm(`¿Eliminar "${filename}"?`)) return;
    setDeleting(prev => ({ ...prev, [docId]: true }));
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Documento eliminado');
      fetchDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(prev => ({ ...prev, [docId]: false }));
    }
  };

  const formatCOP = (v) => v != null ? `$${Number(v).toLocaleString('es-CO')}` : '-';

  return (
    <div className="space-y-6" data-testid="duplicates-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 tracking-tight flex items-center gap-3">
            <ShieldAlert className="text-rose-600" size={40} />
            Duplicados
          </h1>
          <p className="text-zinc-500 mt-2">
            {loading ? 'Analizando...' : (
              groups.length === 0
                ? 'No se detectaron duplicados ✅'
                : <><span className="font-semibold text-rose-600">{groups.length} grupos</span> con <span className="font-semibold">{totalDuplicates} documentos</span> potencialmente duplicados</>
            )}
          </p>
        </div>
        <Button
          onClick={fetchDuplicates}
          disabled={loading}
          variant="outline"
          data-testid="refresh-duplicates-btn"
        >
          {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
          Re-analizar
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-zinc-400" size={32} />
        </div>
      ) : groups.length === 0 ? (
        <Card className="border-2 border-emerald-200 bg-emerald-50">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldAlert size={32} className="text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-emerald-900">Sin duplicados detectados</h3>
            <p className="text-emerald-700 mt-2">Todos los documentos son únicos. Buen trabajo de control.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.group_id} className="border-2 border-rose-200" data-testid={`duplicate-group-${g.group_id}`}>
              <CardContent className="p-0">
                <div className="bg-rose-50 px-5 py-3 border-b border-rose-200 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle size={20} className="text-rose-600 flex-shrink-0" />
                    <Badge className={`${reasonStyles[g.reason] || 'bg-zinc-100 text-zinc-700'} font-semibold`}>
                      {g.reason}
                    </Badge>
                    <span className="text-sm text-zinc-600">{g.count} documentos</span>
                  </div>
                </div>
                <div className="divide-y divide-zinc-100">
                  {g.documents.map((d, idx) => (
                    <div
                      key={d.id}
                      className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-zinc-50"
                      data-testid={`duplicate-doc-${d.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
                          {idx === 0 ? (
                            <FileText size={16} className="text-zinc-700" />
                          ) : (
                            <Copy size={16} className="text-rose-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-zinc-900 truncate" title={d.filename}>
                            {d.filename}
                            {idx === 0 && <Badge variant="outline" className="ml-2 text-xs">Original</Badge>}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-xs text-zinc-500">
                            {d.tipo_documento && <span className="capitalize">{d.tipo_documento.replace(/_/g, ' ')}</span>}
                            {d.tercero && <span>• {d.tercero}</span>}
                            {d.numero_documento && <span>• N° {d.numero_documento}</span>}
                            {d.valor != null && <span className="text-emerald-600 font-medium">• {formatCOP(d.valor)}</span>}
                            {d.fecha && <span>• {d.fecha}</span>}
                            {d.batch_id && <Badge variant="outline" className="text-[10px]">en lote</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDetailDocId(d.id)}
                          className="h-8 w-8 p-0"
                          title="Ver ficha"
                          data-testid={`view-details-${d.id}`}
                        >
                          <Eye size={14} />
                        </Button>
                        {!d.batch_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteDoc(d.id, d.filename)}
                            disabled={deleting[d.id]}
                            className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                            title="Eliminar duplicado"
                            data-testid={`delete-duplicate-${d.id}`}
                          >
                            {deleting[d.id] ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DocumentDetailsModal
        docId={detailDocId}
        open={!!detailDocId}
        onClose={() => setDetailDocId(null)}
      />
    </div>
  );
};

export default Duplicates;
